import React from 'react';
import { Link } from 'react-router-dom';
import { Home, ArrowLeft, Search, Podcast, BookOpen, Video, MessageCircle } from 'lucide-react';
import SEO from '../components/SEO';

const NotFound = () => {
  const popularPages = [
    { name: 'Free SEO Audit', path: '/free-seo-audit', icon: Search, description: 'Check your website health in 30 seconds' },
    { name: 'Websites & Apps', path: '/website-mobile', icon: Video, description: 'SEO-ready websites from £995' },
    { name: 'Your Journey', path: '/your-journey', icon: Podcast, description: 'See the 6-step visibility roadmap' },
    { name: 'Our Blog', path: '/blog', icon: BookOpen, description: 'Tips, guides & SEO insights' },
  ];

  return (
    <>
      <SEO 
        title="Page Not Found | Audio & Co®"
        description="The page you're looking for doesn't exist. Explore our podcast production, YouTube, and book publishing services."
        noIndex={true}
      />
      
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex items-center justify-center px-4 py-16">
        <div className="max-w-2xl w-full text-center">
          {/* 404 Visual */}
          <div className="relative mb-8">
            <div className="text-[180px] md:text-[220px] font-black text-gray-800 leading-none select-none">
              404
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-red-600 text-white px-6 py-3 rounded-full text-lg font-semibold transform -rotate-12 shadow-lg">
                Page Not Found
              </div>
            </div>
          </div>

          {/* Message */}
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Oops! This page has gone off-air
          </h1>
          <p className="text-gray-400 text-lg mb-8 max-w-md mx-auto">
            The page you're looking for might have been moved, deleted, or never existed. 
            Let's get you back on track.
          </p>

          {/* Primary Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              data-testid="404-home-button"
            >
              <Home className="w-5 h-5" />
              Back to Homepage
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors border border-gray-700"
              data-testid="404-contact-button"
            >
              <MessageCircle className="w-5 h-5" />
              Contact Us
            </Link>
          </div>

          {/* Popular Pages */}
          <div className="border-t border-gray-800 pt-8">
            <h2 className="text-lg font-semibold text-gray-300 mb-6">
              Popular destinations
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {popularPages.map((page) => (
                <Link
                  key={page.path}
                  to={page.path}
                  className="group flex items-start gap-4 p-4 bg-gray-800/50 hover:bg-gray-800 rounded-lg transition-all text-left border border-gray-800 hover:border-gray-700"
                  data-testid={`404-link-${page.path.replace(/\//g, '-')}`}
                >
                  <div className="p-2 bg-red-600/20 rounded-lg group-hover:bg-red-600/30 transition-colors">
                    <page.icon className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <div className="font-semibold text-white group-hover:text-red-400 transition-colors">
                      {page.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {page.description}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Back Link */}
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-300 mt-8 transition-colors"
            data-testid="404-back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Go back to previous page
          </button>
        </div>
      </div>
    </>
  );
};

export default NotFound;
